import { createHttpTransport } from '@fd/api-client';
import { SyncEngine, type EngineStatus } from '@fd/sync-engine';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { AuthClient, AuthState } from '../platform/auth.ts';
import { subscribeDevChanges } from '../platform/realtime.ts';
import { openBrowserDatabase } from '../platform/sqlite-driver.ts';
import { backendHeaders, config, deviceHeaders, deviceId, readPreference, writePreference } from './config.ts';
import { auth } from './session-auth.ts';

// ------------------------------------------------------------------------------------ auth

const AuthContext = createContext<{ auth: AuthClient; state: AuthState } | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(auth.getState());
  useEffect(() => {
    const unsubscribe = auth.subscribe(setState);
    void auth.restore();
    return unsubscribe;
  }, []);
  return <AuthContext.Provider value={{ auth, state }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth outside AuthProvider');
  return context;
}

// ------------------------------------------------------------------------------------ session

type SessionContext = {
  engine: SyncEngine;
  userId: string;
  householdId: string | null;
  setHouseholdId(id: string): void;
  /**
   * Before setting up a new household: the one this account already has on the server, synced
   * and chosen — so a second device (or a second click) never creates a duplicate. Null when
   * there is none or the server cannot be reached.
   */
  adoptExisting(): Promise<string | null>;
  status: EngineStatus;
  persistent: boolean;
  /** Push and pull now (debounced when called in bursts). */
  requestSync(): void;
  queryClient: QueryClient;
};

const Session = createContext<SessionContext | null>(null);

export function useSession(): SessionContext {
  const context = useContext(Session);
  if (!context) throw new Error('useSession outside SessionProvider');
  return context;
}

export function SessionProvider({ userId, children, fallback }: { userId: string; children: ReactNode; fallback: ReactNode }) {
  const [engine, setEngine] = useState<SyncEngine | null>(null);
  const [persistent, setPersistent] = useState(true);
  const [householdId, setHouseholdId] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [status, setStatus] = useState<EngineStatus | null>(null);
  const queryClient = useMemo(
    () => new QueryClient({ defaultOptions: { queries: { staleTime: Infinity, refetchOnWindowFocus: false, retry: false } } }),
    [],
  );
  const syncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const householdRef = useRef<string | null>(null);
  householdRef.current = householdId;
  const transportRef = useRef<ReturnType<typeof createHttpTransport> | null>(null);

  /** The household this device shows: the one picked last on this device, else the oldest. */
  const choose = (households: readonly { id: string; createdAt?: unknown }[]) => {
    const remembered = readPreference(`household.${userId}`);
    if (remembered && households.some((h) => h.id === remembered)) return remembered;
    const oldest = [...households].sort((a, b) => String(a.createdAt ?? a.id).localeCompare(String(b.createdAt ?? b.id)))[0];
    return oldest?.id ?? null;
  };
  const selectHousehold = useCallback(
    (id: string) => {
      writePreference(`household.${userId}`, id);
      setHouseholdId(id);
    },
    [userId],
  );

  // Open the local database and start the engine for this user.
  useEffect(() => {
    let cancelled = false;
    // Undone when the user changes (sign-out, another account): the old engine must not go on
    // refreshing the screens of the new one.
    const unsubscribe: (() => void)[] = [];
    void (async () => {
      const { driver, persistent: isPersistent } = await openBrowserDatabase(`finance-${userId}`);
      const transport = createHttpTransport({
        baseUrl: `${config.backendUrl}/functions/v1/sync`,
        getAccessToken: () => auth.accessToken(),
        deviceId: deviceId(),
        // How this device appears in Settings → Devices.
        headers: { ...backendHeaders(), ...deviceHeaders() },
      });
      transportRef.current = transport;
      const created = new SyncEngine({ driver, transport, deviceId: deviceId() });
      await created.start();
      if (cancelled) return;
      unsubscribe.push(
        created.subscribeStatus(setStatus),
        created.subscribe((event) => {
          for (const type of event.entityTypes) void queryClient.invalidateQueries({ queryKey: ['entities', type] });
          void queryClient.invalidateQueries({ queryKey: ['derived'] });
        }),
      );
      setPersistent(isPersistent);
      setEngine(created);

      // Households: what the cache has, else what the server says after a first sync.
      let households = await created.households();
      if (households.length === 0) {
        try {
          const memberships = await transport.memberships();
          const ids = memberships.households.map((m) => m.householdId);
          if (ids.length > 0) await created.sync(ids);
        } catch {
          // offline on first start: onboarding or the cached household will do
        }
        households = await created.households();
      }
      if (!cancelled) {
        setHouseholdId(choose(households));
        setReady(true);
      }
    })();
    return () => {
      cancelled = true;
      for (const stop of unsubscribe) stop();
    };
  }, [userId, queryClient]);

  // Background sync: on start, periodically, when the network or the window comes back.
  useEffect(() => {
    if (!engine || !householdId) return;
    const run = async () => {
      await engine.wakeQueued();
      await engine.sync([householdId]);
    };
    void run();
    // With Supabase there is no long-poll: a look every 20 s while the app is in view instead
    // (a few calls a minute, well within the free plan).
    const interval = setInterval(() => {
      if (!config.supabase || document.visibilityState === 'visible') void run();
    }, config.supabase ? 20_000 : 60_000);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void run();
    };
    addEventListener('online', onVisible);
    addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    const realtime = config.supabase
      ? { close: () => undefined }
      : subscribeDevChanges({
          baseUrl: config.backendUrl,
          householdId,
          getAccessToken: () => auth.accessToken(),
          onChange: () => void engine.sync([householdId]),
        });
    return () => {
      clearInterval(interval);
      removeEventListener('online', onVisible);
      removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
      realtime.close();
    };
  }, [engine, householdId]);

  const value = useMemo<SessionContext | null>(
    () =>
      engine && status
        ? {
            engine,
            userId,
            householdId,
            setHouseholdId: selectHousehold,
            async adoptExisting() {
              try {
                const memberships = await transportRef.current?.memberships();
                const ids = memberships?.households.map((m) => m.householdId) ?? [];
                if (ids.length === 0) return null;
                await engine.sync(ids);
                const chosen = choose(await engine.households());
                if (chosen) selectHousehold(chosen);
                return chosen;
              } catch {
                return null;
              }
            },
            status,
            persistent,
            queryClient,
            requestSync() {
              if (syncTimer.current) clearTimeout(syncTimer.current);
              syncTimer.current = setTimeout(() => {
                const id = householdRef.current;
                if (id) void engine.sync([id]);
              }, 250);
            },
          }
        : null,
    [engine, status, userId, householdId, persistent, queryClient, selectHousehold],
  );

  if (!value || !ready) return <>{fallback}</>;
  return (
    <QueryClientProvider client={queryClient}>
      <Session.Provider value={value}>{children}</Session.Provider>
    </QueryClientProvider>
  );
}

